import { Directive, Host, OnDestroy, OnInit } from '@angular/core';
import { QuillEditorComponent } from 'ngx-quill';
import { LinkFormattingService } from '../services/link-formatting.service';

/**
 * Automatically verifies and formats HTTPS links in all Quill editors.
 */
@Directive({
  selector: 'quill-editor'
})
export class QuillEditorLinkFormatDirective implements OnInit, OnDestroy {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private contentChangedSub: { unsubscribe: () => void } | null = null;
  private isApplyingFormat = false;

  constructor(
    @Host() private quillEditor: QuillEditorComponent,
    private linkFormattingService: LinkFormattingService
  ) {}

  ngOnInit(): void {
    this.contentChangedSub = this.quillEditor.onContentChanged.subscribe(() => this.scheduleFormatting());
  }

  ngOnDestroy(): void {
    this.contentChangedSub?.unsubscribe();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private scheduleFormatting(): void {
    if (this.isApplyingFormat) {
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => void this.applyFormatting(), 500);
  }

  private async applyFormatting(): Promise<void> {
    const editor = this.quillEditor.quillEditor;
    if (!editor) {
      return;
    }

    const html = editor.root.innerHTML;
    if (!html || html === '<p><br></p>') {
      return;
    }

    const result = await this.linkFormattingService.processNewUrls(html, true);
    if (!result.modified || result.texte === html) {
      return;
    }

    this.isApplyingFormat = true;
    try {
      const selection = editor.getSelection();
      editor.clipboard.dangerouslyPasteHTML(result.texte);
      if (selection) {
        editor.setSelection(selection.index, selection.length);
      }
    } finally {
      this.isApplyingFormat = false;
    }
  }
}
